import { useState } from "react";
import { timeline } from "@/mocks/evidence";
import Reveal from "./Reveal";
import { SectionHeading, StatusBadge, statusStyle } from "./Ui";

export default function Timeline() {
  const [expanded, setExpanded] = useState<string>("tl-1");

  return (
    <section id="timeline" className="bg-background-50">
      <div className="mx-auto max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="Breakdown · 逐项拆解"
          title="实锤时间线 · 争议逐项拆解"
          desc="把事件的每一次关键转折按时间归档，标注其结论等级与待补项。点击条目可展开材料清单与核验要点。"
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
          {/* Left sticky summary */}
          <aside className="order-2 lg:order-1 lg:sticky lg:top-24 lg:self-start">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <div className="border-b border-background-200 bg-background-200/60 p-5">
                <span className="text-xs font-semibold tracking-wide text-primary-700">
                  CASE · 事件拆解档案
                </span>
                <h3 className="mt-2 font-heading text-lg font-semibold text-foreground-950">
                  直播间口径事件复盘
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  以录音流出为起点，逐条跟踪回应、截图与录屏，形成可复核的完整证据链。
                </p>
              </div>
              <div className="space-y-4 p-5">
                <div>
                  <div className="flex items-center justify-between text-xs font-medium text-foreground-600">
                    <span>拆解进度</span>
                    <span className="font-semibold text-foreground-950">4 / 6</span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-background-200">
                    <div className="h-full w-2/3 rounded-full bg-primary-600" />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {["石锤 ×1", "实锤 ×1", "存疑 ×1", "反转 ×1"].map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-foreground-700"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <a
                  href="#about"
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-foreground-800"
                >
                  反馈缺漏与异议
                  <i className="ri-chat-3-line flex h-4 w-4 items-center justify-center text-sm" />
                </a>
              </div>
            </div>
          </aside>

          {/* Right timeline */}
          <div className="order-1 lg:order-2">
            <ol className="relative space-y-4 border-l border-background-200 pl-6">
              {timeline.map((item) => {
                const open = expanded === item.id;
                const st = statusStyle(item.status);
                return (
                  <Reveal key={item.id}>
                    <li className="relative">
                      <span
                        className={`absolute -left-[31px] top-5 flex h-2.5 w-2.5 items-center justify-center rounded-full ring-4 ring-background-50 ${st.dot}`}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setExpanded(open ? "" : item.id)
                        }
                        className={`block w-full text-left rounded-lg border transition-colors ${
                          open
                            ? "border-primary-300 bg-background-50"
                            : "border-background-200 bg-background-100/50 hover:border-primary-200"
                        }`}
                        aria-expanded={open}
                      >
                        <div className="flex items-start justify-between gap-3 p-4 md:p-5">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <time className="text-xs font-semibold text-foreground-500">
                                {item.date}
                              </time>
                              <span className="text-foreground-300">·</span>
                              <span className="text-xs text-foreground-500">
                                {item.time}
                              </span>
                              <StatusBadge status={item.status} />
                            </div>
                            <h3 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-950">
                              {item.title}
                            </h3>
                          </div>
                          <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-background-200 bg-background-50 text-foreground-600">
                            <i
                              className={`flex h-4 w-4 items-center justify-center text-base transition-transform ${
                                open ? "ri-subtract-line" : "ri-add-line"
                              }`}
                            />
                          </span>
                        </div>
                        {open && (
                          <div className="border-t border-background-200 px-4 py-4 md:px-5">
                            <p className="text-sm leading-relaxed text-foreground-700">
                              {item.summary}
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {item.material.map((m) => (
                                <span
                                  key={m}
                                  className="inline-flex items-center gap-1.5 rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-900"
                                >
                                  <i className="ri-file-paper-2-line flex h-4 w-4 items-center justify-center text-sm" />
                                  {m}
                                </span>
                              ))}
                            </div>
                            <div className="mt-4 flex flex-wrap gap-3 text-xs font-semibold">
                              <a
                                href="#library"
                                className="inline-flex items-center gap-1 text-primary-700 hover:text-primary-800"
                              >
                                调阅关联材料
                                <i className="ri-arrow-right-line text-sm" />
                              </a>
                              <a
                                href="#standard"
                                className="inline-flex items-center gap-1 text-foreground-600 hover:text-foreground-900"
                              >
                                查看核验标准
                                <i className="ri-arrow-right-line text-sm" />
                              </a>
                            </div>
                          </div>
                        )}
                      </button>
                    </li>
                  </Reveal>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}